// @ts-strict-ignore
import React, { useState } from 'react'
import MultiStepForm from './multi-step-form'
import { trackEvent, gtmTrack } from '../../utils/tracking'
import { connect } from 'react-redux'
import { readCookieRaw } from '../../utils/cookies'
import { query, brand, consentUrl, pathname } from '../../redux/app-selectors'
import isLocalUrl from '../../server/helpers/is-local-url'
import { callNerdalytics } from '../../utils/segment-tracking'
import determineRedirect from '../../utils/determine-redirect'
import { FsAPI } from '../../types/fs-api'
import { getComponentById } from '../../redux/component/component-selectors'
import VerificationForm from '../../components/component/prepop/verification-form'
import { getValue } from '../../utils/product-schema'
import { matchesCondition } from './utils'
import { generateId } from './utils'
import { eligibilityStatusWithRetry } from './eligibility'

const VariantMultiStepForm = (props: any) => {
  const [isLoading, setLoading] = useState(false)
  const [isErrored, setErrored] = useState(false)
  const [profileExists, setProfileExists] = React.useState(false)

  React.useEffect(() => {
    const fetchProfileExists = async () => {
      const exists = await getProfileExists()
      setProfileExists(exists)
    }

    fetchProfileExists()

    const handlePageShow = (event: { persisted: boolean }) => {
      if (event.persisted) {
        setLoading(false)
      }
    }

    window.addEventListener('pageshow', handlePageShow)

    return () => {
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  const {
    schema: {
      method,
      integrationType,
      formName,
      defaultProduct,
      idType = '',
      steps,
      redirectLinks = [],
      segmentTracking,
      fieldTracking,
      prefill,
      dynamicAction,
    },
    query: { product_id, utm_source },
    consentUrl,
    brand,
    dynamicActionElasticSearchData,
  } = props

  const [skip, setSkip] = React.useState(false)
  const [dobWrong, setDobWrong] = React.useState(false)
  const [profileData, setProfileData] = React.useState(undefined)

  const { __FS_API_URL__ } = global

  const isFormFieldVisible = (field: any, params: any) => {
    if (!field.visibleIf) return true

    return (
      (field.visibleIf.or || []).some((e) => params[e.field] === e.equals) &&
      (field.visibleIf.and || []).every((e) => params[e.field] === e.equals)
    )
  }

  const setQueryParams = (steps: any, params: any, href: URL) => {
    steps?.forEach((step) => {
      step.fields.forEach((field) => {
        if (field.setInQueryParams && isFormFieldVisible(field, params)) {
          href.searchParams.append(field.name, params[field.name].replace(/,/g, ''))
        }
      })
    })
  }

  const doRedirect = (redirect, requestId, params) => {
    const href = new URL(redirect)
    href.searchParams.append('request_id', requestId)

    if (product_id) {
      href.searchParams.append('product_id', product_id)
    }

    setQueryParams(steps, params, href)

    if (isLocalUrl(href)) {
      window.location.href = `${href.pathname}${href.search}`
    } else {
      window.location.href = redirect.toString()
    }
  }

  const sendDataToAPI = async (params: any) => {
    const requestId = generateId(idType)

    const convertedAddresses = convertAddressToApiFormat(steps, params)

    const requestBody: FsAPI.FSRequest = {
      productId: product_id,
      defaultProduct,
      integrationType,
      requestId,
      params: { ...params, ...convertedAddresses },
    }
    setLoading(true)
    try {
      const response = await fetch(
        `${__FS_API_URL__}/form?integrationType=${integrationType}&formName=${formName}&brand=${brand}&deviceId=${readCookieRaw(
          'uscd',
        )}&utm_source=${utm_source ?? ''}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        },
      )
      const result: FsAPI.FSResponse = await response.json()
      const { action, redirectURL } = result

      const redirect = determineRedirect(redirectLinks, params, redirectURL)

      if (response.status === 200) {
        if (action === 'redirect') {
          doRedirect(redirect, requestId, params)
        } else if (action === 'poll') {
          const resultsReady = await eligibilityStatusWithRetry(brand, requestId)
          if (resultsReady) {
            doRedirect(redirect, requestId, params)
          } else {
            setLoading(false)
            setErrored(true)
          }
        }
      } else {
        setLoading(false)
        setErrored(true)
      }
    } catch (error) {
      setLoading(false)
      setErrored(true)
    }
  }

  const convertAddressToApiFormat = (steps: any[], params: any) => {
    const addressLookupKeys = steps
      .flatMap((s) => s.fields)
      .flatMap((f) => (f.type === 'wrapper' ? f.children : f))
      .filter(
        (f) =>
          f.type === 'address-lookup' ||
          f.type === 'address-input' ||
          f.type === 'address-lookup-uswitch',
      )
      .map((f) => f.name)

    return Object.fromEntries(
      new Map(
        addressLookupKeys.map((k) => {
          const addresses = JSON.parse(params[k])
          let addressArr = []

          if (Array.isArray(addresses)) {
            addressArr = [...addresses]
          } else {
            addressArr = [addresses]
          }

          return [k, addressArr]
        }),
      ),
    )
  }

  const addressLookup = (postcode: string, setAddresses: (data: any) => void) => {
    const apiPath: string = __FS_API_URL__ || 'https://money.co.uk/api'

    fetch(`${apiPath}/address/${postcode}`)
      .then((response) => response.json())
      .then((response: any) => {
        setAddresses(response.data)
      })
  }

  const getProfile = async (brand: string, dob: string, setData: (profile: any) => any) => {
    const response = await fetch(
      `${__FS_API_URL__}/profile?deviceId=${readCookieRaw('uscd')}&brand=${brand}&dob=${dob}`,
    )
    if (response.status === 200) {
      const profile = await response.json()
      profile.addresses = JSON.stringify(profile.addresses)
      setData(profile)
    } else {
      setDobWrong(true)
    }
  }

  const getProfileExists = async () => {
    const response = await fetch(
      `${__FS_API_URL__}/profile?deviceId=${readCookieRaw('uscd')}&brand=${brand}`,
    )
    return response.status !== 404
  }

  const dynamicActionSubmit = (params, form) => {
    for (const [i, action] of dynamicAction.entries()) {
      if (action && matchesCondition(action.condition, params)) {
        const newAction = action.value?.valueFrom
          ? getValue(action.value, dynamicActionElasticSearchData[i])
          : action.value
        try {
          for (const [key, value] of new URL(newAction).searchParams.entries()) {
            const hiddenInput = document.createElement('input')
            hiddenInput.value = value
            hiddenInput.type = 'hidden'
            hiddenInput.name = key
            form.current.appendChild(hiddenInput)
          }
        } catch {}

        form.current.action = newAction
        form.current.submit()
        // remove hidden inputs after submitting
        for (const [key] of new URL(newAction).searchParams.entries()) {
          const hiddenInput = document.getElementsByName(key)[0]
          hiddenInput.remove()
        }
        return true
      }
    }
    return true
  }

  const newProps = {
    ...props,
    ...(method === 'API' && { submit: sendDataToAPI }),
    ...(dynamicAction && { submit: dynamicActionSubmit }),
    isErrored,
    isLoading,
    trackEvent,
    trackInteraction: gtmTrack,
    apiPath: consentUrl,
    brand,
    addressLookup,
    profileData,
    ...(segmentTracking && {
      segmentTracking: () => {
        return new Promise((resolve) => {
          callNerdalytics(segmentTracking.action, segmentTracking.properties, resolve, {
            context: { uscd: readCookieRaw('uscd'), utm_source },
          })
        })
      },
    }),
    ...(fieldTracking && {
      fieldTracking: (fieldName: string) => {
        return new Promise((resolve) => {
          callNerdalytics(
            fieldTracking.action,
            { ...fieldTracking.properties, fieldName },
            resolve,
            {
              context: { uscd: readCookieRaw('uscd'), utm_source },
            },
          )
        })
      },
    }),
  }

  const profileNeeded = !skip && prefill && profileExists && !profileData

  if (profileNeeded) {
    return (
      <VerificationForm
        brand={brand}
        verifyDOB={(dob: string) => getProfile(brand, dob, setProfileData)}
        dobWrong={dobWrong}
        heading={'We have your details'}
        subheading={"Please enter your date of birth to confirm it's you"}
        skip={() => setSkip(true)}
      />
    )
  }

  return <MultiStepForm {...newProps} />
}

const ConnectMultiStepForm = connect((state: any, props: any) => ({
  query: query(state),
  consentUrl: consentUrl(state),
  brand: brand(state),
  elasticSearchData: getComponentById(state, props.sys.id)?.elasticSearchData,
  dynamicActionElasticSearchData: getComponentById(state, props.sys.id)
    ?.dynamicActionElasticSearchData,
  url: pathname(state),
}))(VariantMultiStepForm)

export default (props: JSX.IntrinsicAttributes) => <ConnectMultiStepForm {...props} />
